import { TextInput } from '@src/common/components/TextInput';
import { css } from '@src/common/styles/Styles';
import { type FieldValues, get, type Path } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form/dist/types/errors';
import type { UseFormRegister } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

export interface FormTextInputProps<T extends FieldValues> {
  register: UseFormRegister<T>;
  errors?: FieldErrors<T>;
  fieldName: Path<T>;
  multiline?: boolean;
  label?: string;
  disabled?: boolean;
  uppercase?: boolean;
  size?: 'small' | 'medium';
  slotProps?: object;
  placeholder?: string;
  noLabel?: boolean;
  onBlur?: () => void;
}

export const FormTextInput = <T extends FieldValues>({
  register,
  errors,
  fieldName,
  label,
  multiline,
  disabled,
  uppercase,
  size,
  slotProps,
  placeholder,
  noLabel,
  onBlur,
}: FormTextInputProps<T>) => {
  const { t } = useTranslation();
  const ariaLabel = label ? label : t(`fields.${fieldName}`);

  const registeredProps = register(fieldName);
  const fieldError = errors && get(errors, fieldName);

  return (
    <TextInput
      slotProps={slotProps}
      placeholder={placeholder}
      size={size}
      className={fullWidth}
      disabled={disabled}
      multiline={multiline}
      {...registeredProps}
      label={noLabel ? undefined : ariaLabel}
      error={fieldError && t(`formErrors.${fieldError.message}`)}
      inputProps={{
        style: uppercase ? { textTransform: 'uppercase' } : {},
        'aria-label': ariaLabel,
        readOnly: disabled,
      }}
      onBlur={(e) => {
        registeredProps.onBlur(e);
        onBlur?.();
      }}
    />
  );
};

const fullWidth = css({
  flex: 1,
});
